import React from 'react';
import { ComponentSize, InputMessage, TooltipProps } from '../../atoms';

import { getMessage } from './utils';

import InputLabel from '../InputLabel';
import {
  Container,
  LabelContainer,
  MessageContainer,
} from './FormField.styles';

import { LabelPosition } from './types';

export interface FormFieldProps {
  /**
   * Component label
   */
  label: string;
  /**
   * Component label position
   */
  labelPosition?: LabelPosition;
  /**
   * Component input name
   */
  name: string;
  /**
   * Children nodes
   */
  children: React.ReactNode;
  /**
   * Component size
   */
  size?: ComponentSize;
  /**
   * Component disabled state
   */
  disabled?: boolean;
  /**
   * Component error mode
   */
  hasError?: boolean;
  /**
   * Component error message
   */
  error?: string;
  /**
   * Component hint message
   */
  hint?: string;
  /**
   * Component full width indicator
   */
  fullWidth?: boolean;
  /**
   * Add required mark
   */
  required?: boolean;
  /**
   * Tooltip settings
   */
  tooltip?: Omit<TooltipProps, 'children'>;
  /**
   * Disable message container
   */
  disableMessage?: boolean;
}

/**
- The FormField component works as a container that composes other form elements.
**/
const FormField = ({
  label,
  labelPosition = 'top',
  name,
  size = 'medium',
  disabled = false,
  hasError = false,
  error,
  hint,
  fullWidth = false,
  required = false,
  disableMessage = false,
  tooltip,
  children,
}: FormFieldProps) => {
  const { messageType, message } = getMessage({ hint, error, hasError });

  return (
    <Container
      labelPosition={labelPosition}
      fullWidth={fullWidth}
      data-testid={name}
    >
      <LabelContainer size={size} labelPosition={labelPosition}>
        <InputLabel
          label={label}
          size={size}
          htmlFor={name}
          disabled={disabled}
          required={required}
          tooltip={tooltip}
        />
      </LabelContainer>
      <div>
        {children}
        {!disableMessage && (
          <MessageContainer type={messageType}>
            {message && (
              <InputMessage
                message={message}
                disabled={disabled}
                type={messageType}
              />
            )}
          </MessageContainer>
        )}
      </div>
    </Container>
  );
};

export default FormField;
